import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  alpha
} from '@mui/material'
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule'
import colors from 'styles/colors'
import { formatDate } from 'utils/formatDateTime'
import NorthIcon from '@mui/icons-material/North'
import SouthIcon from '@mui/icons-material/South'
import { IconContainer } from 'components/atoms/Icons'
import { useTranslation } from 'react-i18next'
import RowStack from 'components/atoms/container/RowStack'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'

const DashIcon = () => (
  <IconContainer
    icon={<HorizontalRuleIcon />}
    size={24}
    style={{ color: colors.SecondaryUserText }}
  />
)

const ChangeIcon = ({ hasOldScore, oldScore, newScore }) =>
  hasOldScore &&
  (oldScore !== newScore ? (
    <IconContainer
      icon={oldScore > newScore ? <SouthIcon /> : <NorthIcon />}
      size={24}
      style={{
        color: oldScore > newScore ? colors.Watermelon400 : colors.Green400
      }}
    />
  ) : (
    <DashIcon />
  ))

const ChangeBox = ({ changeScore }) => (
  <BoxWithBackground
    sx={{
      height: 24,
      padding: '0px 5px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: changeScore < 0 ? colors.Peach200 : colors.Green200,
      borderRadius: '5px',
      position: 'absolute',
      right: -12,
      top: -12,
      zIndex: 4
    }}>
    <Typography variant='h6'>{`${changeScore > 0 ? '+' : ''}${changeScore}`}</Typography>
  </BoxWithBackground>
)

const tableCellSx = {
  paddingX: 6,
  paddingY: 8,
  alignItems: 'center',
  textAlign: 'center',
  verticalAlign: 'middle',
  backgroundColor: colors.Grey50,
  width: 100,
  '&.MuiTableCell-root': {
    borderRight: '1px solid ' + colors.Grey25,
    borderBottom: '1px solid ' + colors.Grey25
  }
}

const table1stCellSx = {
  ...tableCellSx,
  textAlign: 'left',
  width: 'auto'
}

const AssessmentTableRow = ({
  name,
  oldScore,
  newScore,
  accentColor,
  handleRowClick,
  hasOlderScore = false
}) => {
  const rowChangeScore =
    oldScore !== '-' && newScore !== '-' ? newScore - oldScore : 0
  const bodyCellSx = {
    ...tableCellSx,
    height: 62,
    backgroundColor: colors.White
  }
  const ScoreCell = ({ score, sx = {} }) => (
    <TableCell sx={[bodyCellSx, sx]}>
      {score === '-' ? (
        <DashIcon />
      ) : (
        <Typography fontWeight={500}>{score}</Typography>
      )}
    </TableCell>
  )

  return (
    <TableRow key={name}>
      <TableCell
        data-testid={`change-table-${name}`}
        onClick={handleRowClick ? handleRowClick : null}
        sx={[
          table1stCellSx,
          { backgroundColor: colors.White },
          handleRowClick && {
            '&:hover': {
              cursor: 'pointer',
              backgroundColor: alpha(accentColor, 0.4)
            }
          }
        ]}>
        <Typography>{name}</Typography>
      </TableCell>

      <ScoreCell
        score={newScore}
        sx={{ backgroundColor: alpha(accentColor, 0.1) }}
      />

      {hasOlderScore && (
        <>
          <ScoreCell score={oldScore} />

          <TableCell sx={bodyCellSx}>
            <RowStack spacing={1} justifyContent={'center'}>
              {rowChangeScore !== 0 ? (
                <>
                  <ChangeIcon
                    hasOldScore={hasOlderScore}
                    newScore={newScore}
                    oldScore={oldScore}
                  />

                  <Typography fontWeight={500}>
                    {`${Math.abs(rowChangeScore?.toFixed(2))}%`}
                  </Typography>
                </>
              ) : (
                <DashIcon />
              )}
            </RowStack>
          </TableCell>
        </>
      )}
    </TableRow>
  )
}

const AssessmentTable = ({
  displayData,
  hasOlderScore,
  showBehaviors = true,
  state,
  behaviourInfoData,
  handleRowClick
}) => {
  const { t, i18n } = useTranslation()
  const accentColor =
    state === 'self'
      ? alpha(colors.Teal400, 0.5)
      : alpha(colors.MidnightTeal300, 0.5)

  const heading =
    state === 'self'
      ? 'assessments.selfAssessment'
      : 'assessments.othersAssessment'
  const changeScore = displayData.changeScore
    ? parseFloat(displayData.changeScore)
    : 0

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {changeScore !== 0 && <ChangeBox changeScore={displayData.changeScore} />}
      <TableContainer
        sx={{
          borderRadius: 2,
          boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.20)'
        }}>
        <Table>
          {showBehaviors && (
            <TableHead>
              <TableRow>
                <TableCell
                  sx={[table1stCellSx, { backgroundColor: accentColor }]}>
                  <Typography variant='h3'>{t(heading)}</Typography>
                </TableCell>

                <TableCell sx={[tableCellSx, { backgroundColor: accentColor }]}>
                  <Typography variant='h3'>
                    {displayData.newScore || 'N/A'}
                  </Typography>
                </TableCell>
                {hasOlderScore && (
                  <>
                    <TableCell sx={tableCellSx}>
                      <Typography variant='h3'>
                        {displayData.oldScore || 'N/A'}
                      </Typography>
                    </TableCell>

                    <TableCell
                      sx={[
                        tableCellSx,
                        { position: 'relative' },
                        changeScore !== 0 && {
                          backgroundColor:
                            changeScore < 0 ? colors.Peach100 : colors.Green100
                        }
                      ]}>
                      <RowStack spacing={0} justifyContent={'center'}>
                        {changeScore !== 0 ? (
                          <>
                            <ChangeIcon
                              hasOldScore={hasOlderScore}
                              newScore={displayData.newScore}
                              oldScore={displayData.oldScore}
                            />
                            <Typography variant='h3'>
                              {`${Math.abs(displayData.changePercent)}%`}
                            </Typography>
                          </>
                        ) : (
                          <DashIcon />
                        )}
                      </RowStack>
                    </TableCell>
                  </>
                )}
              </TableRow>
              <TableRow>
                <TableCell
                  sx={[table1stCellSx, { backgroundColor: colors.Grey25 }]}>
                  <Typography variant='eyebrow'>
                    {t('assessmentTimeline.behaviours')}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={[
                    tableCellSx,
                    { minWidth: 120, backgroundColor: alpha(accentColor, 0.3) }
                  ]}>
                  <Typography variant='eyebrow'>
                    {formatDate(
                      displayData.newTimeStamp,
                      i18n.resolvedLanguage,
                      'short'
                    )}
                  </Typography>
                </TableCell>
                {hasOlderScore && (
                  <>
                    <TableCell
                      sx={[
                        tableCellSx,
                        { minWidth: 120, backgroundColor: colors.Grey25 }
                      ]}>
                      <Typography variant='eyebrow'>
                        {formatDate(
                          displayData.oldTimeStamp,
                          i18n.resolvedLanguage,
                          'short'
                        )}
                      </Typography>
                    </TableCell>

                    <TableCell
                      sx={[tableCellSx, { backgroundColor: colors.Grey25 }]}>
                      <Typography variant='eyebrow' color='textSecondary'>
                        {t('assessmentTimeline.trend')}
                      </Typography>
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {behaviourInfoData[state]?.data.map(
              ([behavior, oldScore, newScore]) => {
                return (
                  <AssessmentTableRow
                    key={behavior}
                    name={behavior}
                    newScore={newScore}
                    oldScore={oldScore}
                    accentColor={accentColor}
                    handleRowClick={() => handleRowClick(behavior)}
                    hasOlderScore={hasOlderScore}
                  />
                )
              }
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default AssessmentTable
